import * as PIXI from 'pixi.js'
import { MetricsTracker, formatTime, formatTimeDetailed } from './metrics'
import videoSrc from './assets/mov_bbb.mp4'

interface CuePoint {
  id: number
  time: number
  triggered: boolean
}

type UpdateMode = 'ticker' | 'rvfc'
type PlaybackState = 'loading' | 'ready' | 'playing' | 'paused'

interface VideoFrameCallbackMetadata {
  presentationTime: number
  expectedDisplayTime: number
  width: number
  height: number
  mediaTime: number
  presentedFrames: number
  processingDuration: number
}

class PixiVideoDemo {
  private app: PIXI.Application
  private video: HTMLVideoElement
  private videoTexture: PIXI.Texture | null = null
  private videoSprite: PIXI.Sprite | null = null
  private hudContainer: PIXI.Container
  private hudText: PIXI.Text
  private cueNotification: PIXI.Text
  private bouncingOrb: PIXI.Graphics
  private frameBorder: PIXI.Graphics

  private metricsTracker: MetricsTracker
  private logs: string[] = []
  private maxLogs = 10

  private state: PlaybackState = 'loading'
  private updateMode: UpdateMode = 'ticker'
  private assumedFps = 30
  private isMuted = false
  private isInitialized = false

  private cuePoints: CuePoint[] = [
    { id: 1, time: 1.0, triggered: false },
    { id: 2, time: 3.5, triggered: false },
    { id: 3, time: 7.25, triggered: false }
  ]

  private pauseAtTarget: number | null = null
  private loopEnabled = false
  private loopStart = 2
  private loopEnd = 5
  private loopCount = 0

  private rvfcHandle: number | null = null
  private rvfcSupported = false
  private rvfcFrameCount = 0
  private playStartTime = 0
  private orbAngle = 0

  private seekSlider: HTMLInputElement
  private isDraggingSlider = false

  constructor() {
    this.metricsTracker = new MetricsTracker()

    this.app = new PIXI.Application({
      width: 800,
      height: 450,
      backgroundColor: 0x1a1a2e,
      antialias: true
    })

    const container = document.getElementById('canvas-container')!
    container.appendChild(this.app.view as HTMLCanvasElement)
    ;(this.app.view as HTMLCanvasElement).id = 'pixi-canvas'

    this.video = this.createVideoElement()

    this.hudContainer = new PIXI.Container()
    this.hudText = new PIXI.Text('Initializing...', {
      fontFamily: 'Consolas, monospace',
      fontSize: 14,
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 3
    })
    this.hudText.position.set(10, 10)
    this.hudContainer.addChild(this.hudText)

    this.cueNotification = new PIXI.Text('', {
      fontFamily: 'Arial',
      fontSize: 24,
      fill: 0xe94560,
      stroke: 0x000000,
      strokeThickness: 4,
      fontWeight: 'bold'
    })
    this.cueNotification.anchor.set(0.5)
    this.cueNotification.position.set(this.app.screen.width / 2, 60)
    this.cueNotification.alpha = 0
    this.hudContainer.addChild(this.cueNotification)

    this.bouncingOrb = new PIXI.Graphics()
    this.bouncingOrb.beginFill(0xe94560)
    this.bouncingOrb.drawCircle(0, 0, 15)
    this.bouncingOrb.endFill()
    this.bouncingOrb.position.set(this.app.screen.width - 40, this.app.screen.height / 2)

    this.frameBorder = new PIXI.Graphics()

    this.app.stage.addChild(this.frameBorder)
    this.app.stage.addChild(this.bouncingOrb)
    this.app.stage.addChild(this.hudContainer)

    this.seekSlider = document.getElementById('seek-slider') as HTMLInputElement

    this.rvfcSupported = 'requestVideoFrameCallback' in HTMLVideoElement.prototype
    this.updateRvfcStatus()

    this.setupEventListeners()
    this.setupUIControls()
    this.setupKeyboardShortcuts()
    this.setupTicker()

    this.log('Demo initialized')
  }

  private createVideoElement(): HTMLVideoElement {
    const video = document.createElement('video')
    video.src = videoSrc
    video.crossOrigin = 'anonymous'
    video.playsInline = true
    video.muted = true
    video.loop = false
    video.preload = 'metadata'
    video.style.display = 'none'
    document.body.appendChild(video)
    return video
  }

  private setupEventListeners(): void {
    this.video.addEventListener('loadedmetadata', () => {
      this.log(`Metadata loaded: ${formatTime(this.video.duration)}`)
      this.seekSlider.max = this.video.duration.toString()
      this.updateState('ready')
    })

    this.video.addEventListener('play', () => {
      this.log('Play started')
      this.updateState('playing')
      this.playStartTime = this.video.currentTime
      this.rvfcFrameCount = 0
      this.resetCuePoints()

      if (this.updateMode === 'rvfc') {
        this.startRvfc()
      }
    })

    this.video.addEventListener('pause', () => {
      this.log(`Paused at ${formatTimeDetailed(this.video.currentTime)}`)
      this.updateState('paused')
      this.stopRvfc()
    })

    this.video.addEventListener('seeked', () => {
      this.updateTexture()
      this.resetCuePoints()
    })

    this.video.addEventListener('ended', () => {
      this.log('Video ended')
      this.updateState('paused')
    })

    this.video.addEventListener('error', () => {
      this.log(`Error: ${this.video.error?.message || 'Unknown error'}`)
    })

    const overlay = document.getElementById('init-overlay')!
    overlay.addEventListener('click', () => this.initialize())
  }

  private async initialize(): Promise<void> {
    if (this.isInitialized) return

    const overlay = document.getElementById('init-overlay')!

    try {
      await this.video.play()
      this.video.pause()
      this.video.currentTime = 0

      this.createVideoTexture()

      this.isInitialized = true
      overlay.classList.add('hidden')
      this.log('Video initialized successfully')
      this.updateState('ready')
    } catch (error) {
      this.log(`Initialization failed: ${(error as Error).message}`)
      overlay.innerHTML = `
        <h2>Playback Error</h2>
        <p>${(error as Error).message}</p>
        <p>Click to retry</p>
      `
    }
  }

  private createVideoTexture(): void {
    this.videoTexture = PIXI.Texture.from(this.video)
    this.videoSprite = new PIXI.Sprite(this.videoTexture)

    const scale = Math.min(
      (this.app.screen.width - 60) / this.video.videoWidth,
      (this.app.screen.height - 60) / this.video.videoHeight
    )

    this.videoSprite.scale.set(scale)
    this.videoSprite.anchor.set(0.5)
    this.videoSprite.position.set(
      this.app.screen.width / 2,
      this.app.screen.height / 2
    )

    this.app.stage.addChildAt(this.videoSprite, 0)

    this.drawBorder()
  }

  private drawBorder(): void {
    if (!this.videoSprite) return

    this.frameBorder.clear()
    this.frameBorder.lineStyle(3, 0xe94560, 1)

    const bounds = this.videoSprite.getBounds()
    this.frameBorder.drawRect(
      bounds.x - 5,
      bounds.y - 5,
      bounds.width + 10,
      bounds.height + 10
    )
  }

  private updateTexture(): void {
    if (!this.videoTexture || !this.videoSprite) return

    this.videoTexture.update()
    this.metricsTracker.recordTextureUpdate()
  }

  private startRvfc(): void {
    if (!this.rvfcSupported) return

    const videoWithRvfc = this.video as HTMLVideoElement & {
      requestVideoFrameCallback: (
        callback: (now: number, metadata: VideoFrameCallbackMetadata) => void
      ) => number
    }

    const callback = (_now: number, metadata: VideoFrameCallbackMetadata) => {
      this.rvfcFrameCount++
      this.metricsTracker.recordFrameCallback(metadata.mediaTime, metadata.expectedDisplayTime)
      this.updateTexture()

      if (!this.video.paused && !this.video.ended) {
        this.rvfcHandle = videoWithRvfc.requestVideoFrameCallback(callback)
      }
    }

    this.rvfcHandle = videoWithRvfc.requestVideoFrameCallback(callback)
  }

  private stopRvfc(): void {
    if (this.rvfcHandle !== null && this.rvfcSupported) {
      const videoWithRvfc = this.video as HTMLVideoElement & {
        cancelVideoFrameCallback: (handle: number) => void
      }
      videoWithRvfc.cancelVideoFrameCallback(this.rvfcHandle)
      this.rvfcHandle = null
    }
  }

  private setupTicker(): void {
    let lastThrottleUpdate = 0
    const throttleInterval = 1000 / 30

    this.app.ticker.add((delta) => {
      const deltaMs = this.app.ticker.deltaMS
      this.metricsTracker.updatePixiFps(deltaMs)

      this.orbAngle += 0.05 * delta
      this.bouncingOrb.position.y =
        this.app.screen.height / 2 + Math.sin(this.orbAngle) * 50

      if (this.cueNotification.alpha > 0) {
        this.cueNotification.alpha -= 0.01 * delta
      }

      if (!this.isInitialized) return

      this.checkCuePoints()
      this.checkPauseAtTarget()
      this.checkLoopSegment()

      if (this.updateMode === 'ticker') {
        this.updateTexture()
      } else if (this.updateMode === 'rvfc' && !this.rvfcSupported) {
        const now = performance.now()
        if (now - lastThrottleUpdate >= throttleInterval) {
          if (!this.video.paused) {
            this.updateTexture()
          }
          lastThrottleUpdate = now
        }
      }

      if (!this.isDraggingSlider) {
        this.seekSlider.value = this.video.currentTime.toString()
        this.updateSeekTimeDisplay()
      }

      this.updateHUD()
      this.updateMetricsDisplay()
      this.updateTimeDisplay()

      const elapsed = this.video.currentTime - this.playStartTime
      if (elapsed > 0 && this.rvfcSupported) {
        this.metricsTracker.calculateDroppedFrames(
          this.assumedFps,
          elapsed,
          this.rvfcFrameCount
        )
      }
    })
  }

  private updateHUD(): void {
    const modeLabel = this.updateMode === 'ticker' ? 'Ticker' :
      (this.rvfcSupported ? 'RVFC' : 'Throttled')

    this.hudText.text = [
      `Time: ${formatTimeDetailed(this.video.currentTime)}`,
      `State: ${this.state.toUpperCase()}`,
      `Mode: ${modeLabel}`,
      this.loopEnabled ? `Loop: ${this.loopStart}s-${this.loopEnd}s (#${this.loopCount})` : ''
    ].filter(Boolean).join('\n')
  }

  private checkCuePoints(): void {
    if (this.video.paused) return

    for (const cue of this.cuePoints) {
      if (!cue.triggered && this.video.currentTime >= cue.time) {
        cue.triggered = true
        this.showCueNotification(cue)
        this.log(`Cue reached: #${cue.id} at ${cue.time}s`)
      }
    }
  }

  private showCueNotification(cue: CuePoint): void {
    this.cueNotification.text = `Cue #${cue.id} at ${cue.time}s`
    this.cueNotification.alpha = 1
  }

  private resetCuePoints(): void {
    for (const cue of this.cuePoints) {
      cue.triggered = this.video.currentTime >= cue.time
    }
  }

  private checkPauseAtTarget(): void {
    if (this.pauseAtTarget === null || this.video.paused) return

    if (this.video.currentTime >= this.pauseAtTarget) {
      const actual = this.video.currentTime
      const target = this.pauseAtTarget
      const error = actual - target

      this.video.pause()
      this.pauseAtTarget = null

      const resultEl = document.getElementById('pause-result')!
      resultEl.innerHTML = `
        Target: ${target.toFixed(3)}s<br>
        Actual: ${actual.toFixed(3)}s<br>
        Error: ${(error * 1000).toFixed(2)}ms
      `
      this.log(`Paused at target: ${actual.toFixed(3)}s (error: ${(error * 1000).toFixed(2)}ms)`)
    }
  }

  private checkLoopSegment(): void {
    if (!this.loopEnabled || this.video.paused) return

    if (this.video.currentTime >= this.loopEnd) {
      this.video.currentTime = this.loopStart
      this.loopCount++
      document.getElementById('loop-count')!.textContent = `Loop count: ${this.loopCount}`
      this.log(`Loop ${this.loopCount}: jumped to ${this.loopStart}s`)
    }
  }

  private updateState(newState: PlaybackState): void {
    this.state = newState
    const badge = document.getElementById('status-badge')!
    badge.textContent = newState.charAt(0).toUpperCase() + newState.slice(1)
    badge.className = `status-badge ${newState}`
  }

  private updateSeekTimeDisplay(): void {
    const el = document.getElementById('seek-time')!
    el.textContent = formatTimeDetailed(parseFloat(this.seekSlider.value)) + 's'
  }

  private updateTimeDisplay(): void {
    const el = document.getElementById('current-time-display')!
    el.textContent = `${formatTime(this.video.currentTime)} / ${formatTime(this.video.duration || 0)}`
  }

  private updateMetricsDisplay(): void {
    const metrics = this.metricsTracker.getMetrics()

    document.getElementById('metric-pixi-fps')!.textContent =
      metrics.pixiFps.toString()
    document.getElementById('metric-updates')!.textContent =
      metrics.updatesPerSecond.toString()
    document.getElementById('metric-playback-rate')!.textContent =
      this.video.playbackRate.toFixed(1) + 'x'
    document.getElementById('metric-dropped')!.textContent =
      metrics.droppedFrames.toString()
  }

  private updateRvfcStatus(): void {
    const el = document.getElementById('rvfc-status')!
    if (this.rvfcSupported) {
      el.textContent = 'requestVideoFrameCallback is supported'
      el.style.color = '#00b894'
    } else {
      el.textContent = 'RVFC not supported, using throttled fallback'
      el.style.color = '#fdcb6e'
    }
  }

  private setupUIControls(): void {
    document.getElementById('btn-play')!.addEventListener('click', () => {
      this.play()
    })

    document.getElementById('btn-pause')!.addEventListener('click', () => {
      this.pause()
    })

    document.getElementById('btn-mute')!.addEventListener('click', () => {
      this.toggleMute()
    })

    this.seekSlider.addEventListener('input', () => {
      this.isDraggingSlider = true
      this.updateSeekTimeDisplay()
    })

    this.seekSlider.addEventListener('change', () => {
      this.seek(parseFloat(this.seekSlider.value))
      this.isDraggingSlider = false
    })

    document.querySelectorAll('.jump-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const time = parseFloat((btn as HTMLButtonElement).dataset.time!)
        this.seek(time)
      })
    })

    document.querySelectorAll('.cue-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const cueId = parseInt((btn as HTMLButtonElement).dataset.cue!)
        const cue = this.cuePoints.find(c => c.id === cueId)
        if (cue) {
          this.seek(cue.time)
        }
      })
    })

    document.getElementById('btn-step-back')!.addEventListener('click', () => {
      this.stepBackward()
    })

    document.getElementById('btn-step-forward')!.addEventListener('click', () => {
      this.stepForward()
    })

    const fpsInput = document.getElementById('fps-input') as HTMLInputElement
    fpsInput.addEventListener('change', () => {
      this.assumedFps = parseInt(fpsInput.value) || 30
      this.updateStepSizeDisplay()
    })
    this.updateStepSizeDisplay()

    document.getElementById('btn-pause-at-target')!.addEventListener('click', () => {
      const targetInput = document.getElementById('target-time') as HTMLInputElement
      this.pauseAtTarget = parseFloat(targetInput.value)
      this.log(`Will pause at ${this.pauseAtTarget}s`)
      document.getElementById('pause-result')!.textContent = `Waiting for ${this.pauseAtTarget}s...`
      this.play()
    })

    document.getElementById('btn-toggle-loop')!.addEventListener('click', () => {
      this.toggleLoop()
    })

    const loopStartInput = document.getElementById('loop-start') as HTMLInputElement
    const loopEndInput = document.getElementById('loop-end') as HTMLInputElement

    loopStartInput.addEventListener('change', () => {
      this.loopStart = parseFloat(loopStartInput.value) || 0
    })

    loopEndInput.addEventListener('change', () => {
      this.loopEnd = parseFloat(loopEndInput.value) || this.video.duration
    })

    document.getElementById('mode-ticker')!.addEventListener('click', () => {
      this.setUpdateMode('ticker')
    })

    document.getElementById('mode-rvfc')!.addEventListener('click', () => {
      this.setUpdateMode('rvfc')
    })
  }

  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return

      switch (e.code) {
        case 'Space':
          e.preventDefault()
          this.togglePlayPause()
          break
        case 'ArrowLeft':
          e.preventDefault()
          this.stepBackward()
          break
        case 'ArrowRight':
          e.preventDefault()
          this.stepForward()
          break
        case 'KeyL':
          e.preventDefault()
          this.toggleLoop()
          break
      }
    })
  }

  private updateStepSizeDisplay(): void {
    const stepSize = 1 / this.assumedFps
    document.getElementById('step-size')!.textContent = `= ${stepSize.toFixed(4)}s/step`
  }

  private setUpdateMode(mode: UpdateMode): void {
    this.updateMode = mode

    document.getElementById('mode-ticker')!.classList.toggle('active', mode === 'ticker')
    document.getElementById('mode-rvfc')!.classList.toggle('active', mode === 'rvfc')

    if (mode === 'rvfc' && !this.video.paused) {
      this.startRvfc()
    } else {
      this.stopRvfc()
    }

    this.metricsTracker.reset()
    this.log(`Update mode changed to: ${mode}`)
  }

  play(): void {
    if (!this.isInitialized) {
      this.initialize()
      return
    }
    this.video.play().catch(err => {
      this.log(`Play failed: ${err.message}`)
    })
  }

  pause(): void {
    this.video.pause()
  }

  togglePlayPause(): void {
    if (this.video.paused) {
      this.play()
    } else {
      this.pause()
    }
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted
    this.video.muted = this.isMuted
    const btn = document.getElementById('btn-mute')!
    btn.textContent = this.isMuted ? 'Unmute' : 'Mute'
    this.log(this.isMuted ? 'Muted' : 'Unmuted')
  }

  seek(time: number): void {
    const clampedTime = Math.max(0, Math.min(time, this.video.duration || 0))
    this.video.currentTime = clampedTime
    this.log(`Seek to ${formatTimeDetailed(clampedTime)}`)
  }

  stepForward(): void {
    const stepSize = 1 / this.assumedFps
    this.pause()
    this.seek(this.video.currentTime + stepSize)
  }

  stepBackward(): void {
    const stepSize = 1 / this.assumedFps
    this.pause()
    this.seek(this.video.currentTime - stepSize)
  }

  toggleLoop(): void {
    this.loopEnabled = !this.loopEnabled
    const btn = document.getElementById('btn-toggle-loop')!
    btn.textContent = this.loopEnabled ? 'Disable Loop (L)' : 'Enable Loop (L)'
    btn.classList.toggle('active', this.loopEnabled)

    if (this.loopEnabled) {
      this.loopCount = 0
      document.getElementById('loop-count')!.textContent = 'Loop count: 0'
    }

    this.log(`Loop segment ${this.loopEnabled ? 'enabled' : 'disabled'}`)
  }

  log(message: string): void {
    const time = new Date().toLocaleTimeString()
    this.logs.unshift({ time, message } as any)
    if (this.logs.length > this.maxLogs) {
      this.logs.pop()
    }

    const container = document.getElementById('log-container')!
    container.innerHTML = this.logs
      .map((l: any) => `<div class="log-entry"><span class="time">${l.time}</span>${l.message}</div>`)
      .join('')
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new PixiVideoDemo()
})
